/**
 * Shared hook for fetching available AI model categories.
 *
 * Used by ModelCategoryPicker across admin settings, agent processing, and chat.
 * Fetches from the ai-chat available-categories endpoint (accessible to all authenticated users).
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/axiosInstance';

export interface ModelCategory {
  id: string;
  name: string;
  description: string | null;
  tier: string | null;
  provider_id: string;
  provider_name: string;
  resolved_model: string | null;
  available: boolean;
  reasoning_supported: boolean;
  /** The effort applied when nobody overrides — the category's default */
  reasoning_level: string | null;
  /**
   * Every effort the RESOLVED MODEL accepts, cheapest first. This is the option
   * list for ReasoningEffortPicker; it is narrowed per model (the `mini`
   * category reasons, gpt-4o-mini does not), so offering anything else would
   * let a user save a level the API silently drops.
   */
  reasoning_allowed_levels: string[];
}

interface AvailableCategoriesResponse {
  categories: ModelCategory[];
  by_provider: Record<string, ModelCategory[]>;
}

export const AVAILABLE_CATEGORIES_QUERY_KEY = 'available-model-categories';

export function useAvailableModelCategories() {
  return useQuery({
    queryKey: [AVAILABLE_CATEGORIES_QUERY_KEY],
    queryFn: async (): Promise<ModelCategory[]> => {
      const response = await apiGet<{ data: AvailableCategoriesResponse }>(
        '/api/v1/ai-chat/available-categories'
      );
      return response.data.categories;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
